import { el } from './dom';
import { icon } from './Icons';
import type { ChronoMoment } from './ChronoPalette';

/**
 * One painted ribbon: a track that is a picture of what it scrubs, an invisible native range input
 * doing all the real input work, and a drawn handle carrying the colour and glyph of the value it
 * sits on.
 *
 * This is the widget, extracted from {@link ChronoBar} so that a second date control can *be* the
 * same control rather than resemble one. Two places now scrub the calendar — the floating bar over
 * the scene and the header of the flora database — and the one thing that must never happen is the
 * two disagreeing about geometry: the track inset, the handle travel, and the CSS variables they
 * key on are a single contract with `chrono.css`, and it is written down once, here.
 *
 * The class owns exactly two pieces of state, both about the pointer rather than about the world:
 *
 * - **Scrub.** Between `pointerdown` on the input and the `pointerup` that ends it, {@link set}
 *   stops assigning `input.value`. A refresh landing mid-drag would otherwise fight the pointer and
 *   the handle would stutter back to wherever the last frame thought it was.
 * - **Wheel.** Non-passive, so a notch over the ribbon scrubs it instead of scrolling the panel
 *   behind it.
 *
 * Everything else it reads and writes goes through the callbacks, which is what keeps it ignorant
 * of whether it is driving the sun or a wall of thumbnails.
 */

export interface ChronoRibbonOptions {
  /** Which ribbon this is. Only used to key CSS — the mechanics are identical. */
  kind: 'time' | 'date';
  /** Accessible name for the range input. */
  label: string;
  min: number;
  max: number;
  step: number;
  /** How far one wheel notch moves the value. */
  wheel: number;
  /** Pixel size of the glyph drawn inside the handle. */
  glyph?: number;
  onInput: (value: number) => void;
  /** The handle was taken hold of, by pointer or by wheel — hosts that fade use it to hold open. */
  onGrab?: () => void;
  /** The drag ended. Not fired for wheel notches, which have no end to wait for. */
  onRelease?: () => void;
}

const DEFAULT_GLYPH = 15;

export class ChronoRibbon {
  /** The ribbon itself. Mount this. */
  readonly root: HTMLElement;
  /** The painted track — assign `backgroundImage` to paint the year or the day onto it. */
  readonly track: HTMLElement;
  /** The real control, exposed so a host can focus it or hang extra listeners on it. */
  readonly input: HTMLInputElement;

  private readonly thumb: HTMLElement;
  private readonly min: number;
  private readonly max: number;
  private readonly glyph: number;
  private readonly detach: (() => void)[] = [];
  private held = false;

  constructor(options: ChronoRibbonOptions) {
    this.min = options.min;
    this.max = options.max;
    this.glyph = options.glyph ?? DEFAULT_GLYPH;

    this.track = el('span', { class: 'lg-chrono__track' });
    this.thumb = el('span', { class: 'lg-chrono__dot lg-chrono__dot--thumb' });
    this.input = el('input', {
      class: 'lg-chrono__input',
      attrs: {
        type: 'range',
        min: String(options.min),
        max: String(options.max),
        step: String(options.step),
        value: String(options.min),
        'aria-label': options.label,
      },
      on: {
        input: (event) => options.onInput(Number((event.target as HTMLInputElement).value)),
        pointerdown: () => {
          this.held = true;
          options.onGrab?.();
        },
      },
    });

    // Order matters: the input sits over the track to take the pointer, and the handle is painted
    // last (and is pointer-transparent) so it draws on top without stealing the drag. Being after
    // the input in document order is also what lets CSS ring the handle when the input has focus.
    this.root = el(
      'div',
      { class: `lg-chrono__ribbon lg-chrono__ribbon--${options.kind}` },
      this.track,
      this.input,
      this.thumb,
    );

    // Non-passive so the notch scrubs the ribbon instead of scrolling whatever is behind it.
    const wheel = (event: WheelEvent): void => {
      event.preventDefault();
      const notch = event.deltaY > 0 ? -1 : 1;
      const next = clamp(Number(this.input.value) + options.wheel * notch, this.min, this.max);
      if (next === Number(this.input.value)) return;
      this.input.value = String(next);
      options.onGrab?.();
      options.onInput(next);
    };
    this.root.addEventListener('wheel', wheel, { passive: false });
    this.detach.push(() => this.root.removeEventListener('wheel', wheel));

    // On the window, not the ribbon: a drag that ends off the track — the pointer runs past the end
    // of it, which is the normal way to reach the last value — still has to release.
    const release = (): void => {
      if (!this.held) return;
      this.held = false;
      options.onRelease?.();
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('pointerup', release);
      window.addEventListener('pointercancel', release);
      this.detach.push(() => window.removeEventListener('pointerup', release));
      this.detach.push(() => window.removeEventListener('pointercancel', release));
    }
  }

  /** True while this handle is under the pointer. Hosts that fade use it to stay open. */
  get scrubbing(): boolean {
    return this.held;
  }

  /**
   * Move the handle to `value` and dress it for where it landed.
   *
   * Safe to call every frame: no DOM is built, and the input's own value is left alone while it is
   * being dragged so a refresh can never fight the pointer.
   */
  set(value: number, moment: ChronoMoment, valueText: string): void {
    this.root.style.setProperty('--t', trackFraction(value, this.min, this.max).toFixed(5));
    paintDot(this.thumb, moment, this.glyph);
    this.input.setAttribute('aria-valuetext', valueText);
    const next = String(value);
    if (!this.held && this.input.value !== next) this.input.value = next;
  }

  dispose(): void {
    for (const off of this.detach) off();
    this.detach.length = 0;
    this.root.remove();
  }
}

/** Dress a handle or a pill glyph: the colour of the moment, and a legible icon on top of it. */
export function paintDot(dot: HTMLElement, moment: ChronoMoment, size: number): void {
  dot.style.background = moment.color;
  dot.style.color = moment.ink;
  const next = icon(moment.icon, size);
  // Re-assigning identical markup would rebuild the SVG on every frame of a drag for no reason.
  if (dot.innerHTML !== next) dot.innerHTML = next;
}

/** Where a value sits along its track, 0–1. */
export function trackFraction(value: number, min: number, max: number): number {
  if (max <= min) return 0;
  return clamp((value - min) / (max - min), 0, 1);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
